<template>
  <div>
    <el-tabs type="card">
      <el-tab-pane label="Purchase Form">
        <div class="grid grid-cols-4 gap-4">
          <base-input label="Select Date">
            <el-date-picker
              v-model="purchase.date"
              type="date"
              value-format="yyyy-MM-dd"
              class="w-full"
            />
          </base-input>

          <base-input label="Select Supplier">
            <el-select v-model="purchase.supplier_id" class="w-full">
              <el-option
                v-for="(item, index) in suppliers"
                :key="index"
                :value="item.supplierId"
                :label="item.name"
              />
            </el-select>
          </base-input>

          <base-input label="Invoice Number">
            <el-input v-model="purchase.invoice_number" class="w-full" />
          </base-input>

          <base-input label="Status">
            <el-select v-model="purchase.status" class="w-full">
              <el-option
                v-for="(status, index) in statuses"
                :key="index"
                :label="status.label"
                :value="status.value"
              />
            </el-select>
          </base-input>
        </div>

        <div class="grid grid-cols-4 gap-4 mt-4">
          <base-input label="Payment Mode">
            <el-select v-model="purchase.payment_mode_id" class="w-full">
              <el-option
                v-for="(mode, index) in paymentModes"
                :key="index"
                :label="mode.label"
                :value="mode.paymentModeId"
              />
            </el-select>
          </base-input>

          <base-input label="Payment Note">
            <el-input
              v-model="purchase.payment_note"
              type="text"
              class="w-full"
            />
          </base-input>

          <base-input label="Total">
            <el-input v-model="purchase.total" type="number" class="w-full" />
          </base-input>

          <base-input label="Paid Amount">
            <el-input
              v-model="purchase.paid_amount"
              type="number"
              class="w-full"
            />
          </base-input>
        </div>

        <div class="row">
          <div class="col-md-12" style="text-align: right">
            <base-button class="mb-4" @click="addItem">Add Item</base-button>
          </div>
        </div>
        <div
          v-for="(item, index) in purchase.items"
          :key="index"
          class="grid grid-cols-6 gap-4 mt-2"
        >
          <base-input label="Product">
            <el-autocomplete
              v-uppercase
              ref="searchProduct"
              v-model="item.searchKeyword"
              :fetch-suggestions="suggestProducts"
              :trigger-on-focus="false"
              class="product-search"
              value-key="name"
              placeholder="Search Product/Barcode"
              tabindex="4"
              @select="handleProductSelect(item)"
            />
          </base-input>

          <base-input label="Quantity">
            <el-input
              v-model.number="item.quantity"
              type="number"
              min="1"
              class="w-full"
            />
          </base-input>

          <base-input label="Price">
            <el-input
              v-model.number="item.price"
              type="number"
              class="w-full"
            />
          </base-input>

          <base-input label="Tax">
            <el-select v-model="item.tax_id" class="w-full">
              <el-option
                v-for="tax in taxes"
                :key="tax.taxId"
                :label="tax.label"
                :value="tax.taxId"
              />
            </el-select>
          </base-input>

          <base-input label="Discount">
            <el-input
              v-model.number="item.discount"
              type="number"
              class="w-full"
            />
          </base-input>

          <div
            v-if="purchase.items.length > 1"
            class="flex items-center justify-center remove-item"
          >
            <span
              @click="removeItem(index)"
              class="text-2xl text-red-500 cursor-pointer "
            >
              <i class="ni ni-fat-remove text-red" />
            </span>
          </div>
        </div>

        <div class="flex justify-end mt-4">
          <base-button
            md
            type="primary"
            @click="submitPurchase"
            native-type="submit"
          >
            {{ this.$route.params.purchaseId ? "Update" : "Save" }}
          </base-button>
        </div>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import {
  Select,
  Option,
  Form,
  Input,
  DatePicker,
  Tabs,
  TabPane,
  Autocomplete
} from "element-ui";
import { mapGetters, mapActions, mapMutations } from "vuex";

export default {
  props: {
    suppliers: Array,
    statuses: Array,
    taxes: Array,
    paymentModes: Array,
    editedPurchase: {
      type: Object,
      required: false,
      default() {
        return {};
      }
    }
  },
  directives: {
    uppercase: {
      inserted: function(el, _, vnode) {
        el.addEventListener("input", async function(e) {
          const modified = e.target.value
            .split(" ")
            .map(i => i.charAt(0).toUpperCase() + i.slice(1))
            .join(" ");
          vnode.componentInstance.$emit("input", modified);
        });
      }
    }
  },

  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    [Form.name]: Form,
    [Input.name]: Input,
    [DatePicker.name]: DatePicker,
    [Autocomplete.name]: Autocomplete,
    [Tabs.name]: Tabs,
    [TabPane.name]: TabPane
  },

  mounted() {
    if (this.editedPurchase && Object.keys(this.editedPurchase).length) {
      this.purchase = JSON.parse(JSON.stringify(this.editedPurchase));
    }
  },

  watch: {
    editedPurchase: {
      handler(newValue) {
        if (newValue && Object.keys(newValue).length) {
          this.purchase = JSON.parse(JSON.stringify(newValue));
        }
      },
      immediate: true,
      deep: true
    }
  },

  data() {
    return {
      searchKeyword: "",
      purchase: {
        supplier_id: "",
        date: new Date().toISOString().split("T")[0], // Default to today
        invoice_number: "",
        status: "",
        payment_mode_id: "",
        payment_note: "",
        total: 0,
        paid_amount: 0,
        items: [
          {
            product_id: "",
            quantity: 1,
            price: 0,
            tax_id: "",
            discount: 0
          }
        ]
      }
    };
  },

  computed: {
    ...mapGetters({
      rawProducts: "product/getProducts"
    }),
    products() {
      return this.rawProducts && this.rawProducts.rows
        ? this.rawProducts.rows
        : [];
    }
  },

  methods: {
    ...mapActions({
      savePurchase: "purchase/savePurchase",
      updatePurchase: "purchase/updatePurchase",
      getProducts: "product/getProducts"
    }),

    ...mapMutations({
      resetPurchase: "purchase/resetPurchase"
    }),

    addItem() {
      this.purchase.items.push({
        product_id: "",
        quantity: 1,
        price: 0,
        tax_id: null,
        discount: 0
      });
    },

    async suggestProducts(query, cb) {
      const result = await this.getProducts({ searchKeyword: query });
      cb(result.rows);
    },

    removeItem(index) {
      this.purchase.items.splice(index, 1);
    },

    formatPayload() {
      return {
        supplier_id: this.purchase.supplier_id,
        date: this.purchase.date,
        invoice_number: this.purchase.invoice_number,
        status: this.purchase.status,
        payment_mode_id: this.purchase.payment_mode_id,
        payment_note: this.purchase.payment_note || "",
        total: this.purchase.total,
        paid_amount: this.purchase.paid_amount,
        items: this.purchase.items.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          tax_id: item.tax_id || null,
          price: item.price,
          discount: item.discount || 0
        })),
        image: this.purchase.image || null
      };
    },

    async submitPurchase() {
      const payload = this.formatPayload();
      // Validation check
      if (
        !this.purchase.supplier_id ||
        !this.purchase.date ||
        !this.purchase.invoice_number
      ) {
        this.$notify({
          message: "Please fill all required fields!",
          type: "error",
          title: "Validation Error"
        });
        return;
      }

      try {
        if (this.$route.params.purchaseId) {
          await this.updatePurchase({
            purchaseId: this.$route.params.purchaseId,
            payload: payload
          });
          this.$notify({
            message: "Purchase Updated Successfully!",
            type: "success",
            title: "Success"
          });
        } else {
          await this.savePurchase(payload);
          this.$notify({
            message: "Purchase Created Successfully!",
            type: "success",
            title: "Success"
          });
        }

        // Redirect after success
        this.$router.push("/purchases/list");
      } catch (error) {
        this.$notify({
          message: "Failed to process purchase. Please try again.",
          type: "error",
          title: "Error"
        });
        console.error("Purchase Error:", error);
      }
    },

    handleProductSelect(item) {
      this.getProducts({
        name: item.searchKeyword
      }).then(res => {
        item.product_id = res.rows[0].productId;
      });
    }
  }
};
</script>

<style>
.is-invalid input {
  border-color: #fb6340;
}
</style>
